import { useEffect } from 'react'
import { createClient } from '@/lib/supabase-client'

declare global {
  interface Window {
    google?: any
  }
}

interface GoogleOneTapProps {
  disabled?: boolean
}

export default function GoogleOneTap({ disabled }: GoogleOneTapProps) {
  const supabase = createClient()
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined

  useEffect(() => {
    if (disabled || !clientId) return

    let cancelled = false

    const initOneTap = () => {
      if (!window.google || cancelled) return

      try {
        const nonce = crypto.randomUUID()

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response: { credential: string }) => {
            if (!response?.credential) {
              // Missing credential – nothing to do, quietly exit
              return
            }

            try {
              // Supabase will verify the token, including nonce checks internally.
              // If the nonce or token is invalid, fallback to OAuth login
              const { error } = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token: response.credential,
              })

              if (error) {
                // Nonce/token mismatch or other auth error – fallback to OAuth login
                console.warn('Google One Tap sign-in error, falling back to OAuth:', error.message)
                // Fallback to regular OAuth login
                const redirectTo = `${window.location.origin}/dashboard`
                await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    redirectTo,
                  },
                })
              } else {
                // Success - redirect to dashboard
                window.location.href = '/dashboard'
              }
            } catch (err) {
              // If signInWithIdToken fails, fallback to OAuth login
              console.warn('Google One Tap sign-in failed, falling back to OAuth:', err)
              try {
                const redirectTo = `${window.location.origin}/dashboard`
                await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    redirectTo,
                  },
                })
              } catch (oauthErr) {
                console.error('OAuth fallback also failed:', oauthErr)
              }
            }
          },
          nonce,
          cancel_on_tap_outside: true,
          auto_select: false,
        })

        window.google.accounts.id.prompt((notification: any) => {
          // If One Tap is not shown or is dismissed, we just continue without errors.
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // No-op – keep UX clean.
          }
        })
      } catch (err) {
        console.warn('Failed to initialize Google One Tap', err)
      }
    }

    const ensureScript = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        initOneTap()
        return
      }

      const existing = document.querySelector<HTMLScriptElement>('script[data-google-one-tap]')
      if (existing) {
        existing.addEventListener('load', initOneTap)
        return
      }

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.dataset.googleOneTap = 'true'
      script.onload = initOneTap
      document.head.appendChild(script)
    }

    ensureScript()

    return () => {
      cancelled = true
    }
  }, [clientId, disabled, supabase])

  // One Tap renders its own UI; this component is invisible.
  return null
}

